/**
 * Inserting variables from the Fields panel — by click (at the cursor) or by drop (at the pointer).
 * Both paths go through insertField, and the node JSON matches what CdmToolbar's Insert buttons
 * build, so a dropped variable is indistinguishable from a typed or toolbar-inserted one.
 */
import { Extension, type Editor, type JSONContent } from '@tiptap/core'
import { Fragment, Slice } from '@tiptap/pm/model'
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state'
import { dropPoint } from '@tiptap/pm/transform'
import { normalizeMonikerKey } from './monikerKey'
import { emptyParagraphAtDrop, emptyParagraphAtSelection } from './emptyParagraph'
import { defaultParagraphFormat, defaultRunFormat, defaultSourceLocation } from './types'

export const FIELD_DRAG_MIME = 'application/x-docmorphia-field'

export const MAX_LOOP_SCAFFOLD_FIELDS = 6

export type FieldPayload =
  | { kind: 'scalar'; path: string; collection?: string }
  | { kind: 'list'; path: string; itemFields?: string[]; alias?: string }

export function placeholderJson(rawName: string): JSONContent {
  return {
    type: 'placeholder',
    attrs: { rawName, normalizedKey: normalizeMonikerKey(rawName), formatters: null, format: defaultRunFormat(), source: defaultSourceLocation() },
  }
}

/**
 * A loop over `collection` whose body is one paragraph of `{{alias.field}}` chips separated by
 * " — ". No fields leaves an empty body paragraph, as the toolbar's Loop button does; a list of
 * primitives (fields === null) gets a single `{{alias}}`.
 */
export function loopScaffoldJson(collection: string, fields: string[] | null, alias = 'item'): JSONContent {
  const chips = fields === null ? [alias] : fields.slice(0, MAX_LOOP_SCAFFOLD_FIELDS).map(f => `${alias}.${f}`)
  const content: JSONContent[] = []
  chips.forEach((rawName, i) => {
    if (i > 0) content.push({ type: 'text', text: ' — ', marks: [{ type: 'runFormat', attrs: { format: defaultRunFormat() } }] })
    content.push(placeholderJson(rawName))
  })
  return {
    type: 'loop',
    attrs: { rawName: collection, normalizedKey: normalizeMonikerKey(collection), itemAlias: alias, source: defaultSourceLocation() },
    content: [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content }],
  }
}

/** The alias of the innermost loop over `collection` enclosing `pos`, or the toolbar default. */
export function aliasAt(state: EditorState, pos: number, collection: string): string {
  const key = normalizeMonikerKey(collection)
  const $pos = state.doc.resolve(pos)
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d)
    if (node.type.name === 'loop' && node.attrs.normalizedKey === key) return node.attrs.itemAlias as string
  }
  return 'item'
}

export function fieldNodeJson(state: EditorState, pos: number, payload: FieldPayload): JSONContent {
  if (payload.kind === 'list') return loopScaffoldJson(payload.path, payload.itemFields ?? null, payload.alias || 'item')
  const rawName = payload.collection ? `${aliasAt(state, pos, payload.collection)}.${payload.path}` : payload.path
  return placeholderJson(rawName)
}

/**
 * Inserts the field at `pos` (a drop) or the selection (a click). Placeholders go inline — wrapped
 * in a new paragraph if the spot is a block boundary; loops go at a block boundary, after the
 * cursor's block like insertBlockAfterCursor, or the boundary nearest a drop — or in place of the
 * empty paragraph the cursor or drop is in.
 */
export function insertField(editor: Editor, payload: FieldPayload, pos?: number): boolean {
  const { state } = editor
  const at = pos ?? state.selection.from
  const json = fieldNodeJson(state, at, payload)
  const $at = state.doc.resolve(at)

  if (json.type === 'placeholder') {
    if (pos === undefined) return editor.chain().focus().insertContent(json).run()
    if ($at.parent.inlineContent) return editor.chain().focus().insertContentAt(at, json).run()
    const para: JSONContent = { type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [json] }
    const target = dropPoint(state.doc, at, new Slice(Fragment.from(state.schema.nodeFromJSON(para)), 0, 0)) ?? at
    return editor.chain().focus().insertContentAt(target, para).run()
  }

  const empty = pos === undefined ? emptyParagraphAtSelection(state.selection) : emptyParagraphAtDrop(state.doc, at)
  if (empty) return editor.chain().focus().insertContentAt(empty, json).run()
  let target: number
  if (pos === undefined && $at.parent.inlineContent && $at.depth >= 2) target = $at.after()
  else target = dropPoint(state.doc, at, new Slice(Fragment.from(state.schema.nodeFromJSON(json)), 0, 0)) ?? at
  return editor.chain().focus().insertContentAt(target, json).run()
}

export function readFieldPayload(dataTransfer: DataTransfer | null): FieldPayload | null {
  const raw = dataTransfer?.getData(FIELD_DRAG_MIME)
  if (!raw) return null
  try {
    const p = JSON.parse(raw) as FieldPayload
    if ((p.kind === 'scalar' || p.kind === 'list') && typeof p.path === 'string' && p.path.trim()) return p
  } catch {
    // Not ours.
  }
  return null
}

export function writeFieldPayload(dataTransfer: DataTransfer, payload: FieldPayload) {
  dataTransfer.setData(FIELD_DRAG_MIME, JSON.stringify(payload))
  dataTransfer.setData('text/plain', `{{${payload.path}}}`)
  dataTransfer.effectAllowed = 'copy'
}

export const FieldDrop = Extension.create({
  name: 'fieldDrop',
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('fieldDrop'),
        props: {
          handleDrop(view, event) {
            const payload = readFieldPayload(event.dataTransfer)
            if (!payload) return false
            if (!editor.isEditable) return true
            const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
            if (!coords) return true
            insertField(editor, payload, coords.pos)
            return true
          },
        },
      }),
    ]
  },
})
